import { supabase } from './supabase';

export const equipmentLifecycleService = {
  // Equipment CRUD
  async getEquipment(gymId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('*')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true)
        ?.order('name');

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getEquipmentById(equipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('*')
        ?.eq('id', equipmentId)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createEquipment(gymId, equipmentData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.insert({ gym_id: gymId, ...equipmentData })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateEquipment(equipmentId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.update(updates)
        ?.eq('id', equipmentId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteEquipment(equipmentId) {
    try {
      const { error } = await supabase
        ?.from('equipment')
        ?.update({ is_active: false })
        ?.eq('id', equipmentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Maintenance Logs
  async getMaintenanceLogs(equipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_maintenance_logs')
        ?.select('*, performed_by:user_profiles(full_name, email)')
        ?.eq('equipment_id', equipmentId)
        ?.order('maintenance_date', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createMaintenanceLog(logData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_maintenance_logs')
        ?.insert(logData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Work Orders
  async getWorkOrders(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('equipment_work_orders')
        ?.select('*, equipment(*), assigned_to:user_profiles!assigned_to(full_name, email), created_by:user_profiles!created_by(full_name, email)')
        ?.eq('gym_id', gymId);

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      if (filters?.equipmentId) {
        query = query?.eq('equipment_id', filters?.equipmentId);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createWorkOrder(workOrderData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_work_orders')
        ?.insert(workOrderData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateWorkOrder(workOrderId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_work_orders')
        ?.update(updates)
        ?.eq('id', workOrderId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Depreciation
  async getDepreciation(equipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_depreciation')
        ?.select('*')
        ?.eq('equipment_id', equipmentId)
        ?.order('calculation_date', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createDepreciation(depreciationData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_depreciation')
        ?.insert(depreciationData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Condition History
  async getConditionHistory(equipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_condition_history')
        ?.select('*, changed_by:user_profiles(full_name, email)')
        ?.eq('equipment_id', equipmentId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Barcode Scans
  async createBarcodeScan(scanData) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_barcode_scans')
        ?.insert(scanData)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getBarcodeScanHistory(equipmentId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment_barcode_scans')
        ?.select('*, scanned_by:user_profiles(full_name, email)')
        ?.eq('equipment_id', equipmentId)
        ?.order('created_at', { ascending: false })
        ?.limit(50);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Analytics
  async getLifecycleAnalytics(gymId) {
    try {
      const { data, error } = await supabase
        ?.from('equipment')
        ?.select('lifecycle_status, status, condition')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (error) throw error;

      const analytics = {
        byLifecycleStatus: {},
        byStatus: {},
        byCondition: {},
        total: data?.length || 0
      };

      data?.forEach((item) => {
        analytics.byLifecycleStatus[item?.lifecycle_status] = 
          (analytics?.byLifecycleStatus?.[item?.lifecycle_status] || 0) + 1;
        analytics.byStatus[item?.status] = 
          (analytics?.byStatus?.[item?.status] || 0) + 1;
        analytics.byCondition[item?.condition] = 
          (analytics?.byCondition?.[item?.condition] || 0) + 1;
      });

      return { data: analytics, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};